import dayjs, { extend } from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import duration from 'dayjs/plugin/duration'
import isoWeek from 'dayjs/plugin/isoWeek'
import isToday from 'dayjs/plugin/isToday'
import isYesterday from 'dayjs/plugin/isYesterday'
import minMax from 'dayjs/plugin/minMax'
import relativeTime from 'dayjs/plugin/relativeTime'
import updateLocale from 'dayjs/plugin/updateLocale'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

const US_TZ_SUFFIX: Record<string, 'pt' | 'mt' | 'ct' | 'et'> = {
	PT: 'pt', PST: 'pt', PDT: 'pt',
	MT: 'mt', MST: 'mt', MDT: 'mt',
	CT: 'ct', CST: 'ct', CDT: 'ct',
	ET: 'et', EST: 'et', EDT: 'et',
}

extend(relativeTime)
extend(updateLocale)
extend(duration)
extend(minMax)
extend(advancedFormat)
extend(isToday)
extend(isYesterday)
extend(isoWeek)
extend(timezone)
extend(utc)

dayjs.updateLocale('en', {
	relativeTime: {
		future: 'in %s',
		past: (input) => (input === 'just now' ? 'just now' : `${input} ago`),
		s: 'just now',
		m: 'a min',
		mm: '%d min',
		h: 'an hour',
		hh: '%d hours',
		d: 'a day',
		dd: '%d days',
		M: 'a month',
		MM: '%d months',
		y: 'a year',
		yy: '%d years',
	},
})

function formatTime(input: Date | string) {
	const date = dayjs(input)

	if (date.isToday()) {
		return `Today at ${date.format('h:mm A')}`
	} else if (date.isYesterday()) {
		return `Yesterday at ${date.format('h:mm A')}`
	} else if (dayjs().diff(date, 'day') < 7) {
		return `${date.format('dddd')} at ${date.format('h:mm A')}`
	}
	return `${date.format('MMM D')} at ${date.format('h:mm A')}`
}

function getDateGroup(input: Date | string): string {
	const date = dayjs(input)
	const today = dayjs()

	if (date.isSame(today, 'day')) {
		return 'Today'
	} else if (date.isSame(today.subtract(1, 'day'), 'day')) {
		return 'Yesterday'
	} else if (date.isAfter(today.startOf('week'))) {
		return 'This Week'
	} else if (date.isAfter(today.subtract(1, 'week').startOf('week'))) {
		return 'Last Week'
	} else if (date.isAfter(today.startOf('month'))) {
		return 'This Month'
	} else if (date.isAfter(today.subtract(1, 'month').startOf('month'))) {
		return 'Last Month'
	}
	return 'Older'
}

const dateGroupOrder = ['Today', 'Yesterday', 'This Week', 'Last Week', 'This Month', 'Last Month', 'Older']

/**
 * Get browser timezone
 * @returns IANA timezone identifier (e.g., 'America/New_York')
 */
export const getBrowserTimezone = (): string => {
	return Intl.DateTimeFormat().resolvedOptions().timeZone
}

/**
 * Get the US timezone region suffix using the browser's Intl API.
 * Uses `shortGeneric` timeZoneName to get region abbreviations (PT, MT, CT, ET)
 * or specific forms for non-DST regions (e.g. MST for Arizona).
 * Both are mapped to a suffix via US_TZ_SUFFIX.
 *
 * @returns 'pt' | 'mt' | 'ct' | 'et' or null if not a supported US timezone
 */
export const getTimezoneSuffix = (ianaTimezone: string): 'pt' | 'mt' | 'ct' | 'et' | null => {
	try {
		const parts = new Intl.DateTimeFormat('en-US', {
			timeZone: ianaTimezone,
			timeZoneName: 'shortGeneric',
		}).formatToParts(new Date())

		const tzAbbr = parts.find((p) => p.type === 'timeZoneName')?.value ?? ''
		return US_TZ_SUFFIX[tzAbbr] ?? null
	} catch {
		return null
	}
}

export { dayjs, formatTime, getDateGroup, dateGroupOrder }
